import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import deadlineService from '../../services/deadlineService';
import { useAttorney } from '../../context/AttorneyContext';
import MessageComposeModal from '../ClientMessages/MessageComposeModal';
import { RequestViaMarkoTMModal } from '../MarkoTMResponses';
import './DraftReviewModal.css';

/**
 * Feedback categories for AI Learning
 * When attorney modifies a draft, capture what needed improvement
 */
const FEEDBACK_CATEGORIES = {
  content: [
    { id: 'missing_argument', label: 'Missing key argument' },
    { id: 'wrong_argument', label: 'Wrong reasoning' },
    { id: 'irrelevant_content', label: 'Irrelevant content' },
    { id: 'missing_evidence_request', label: 'Missing evidence request' }
  ],
  tone: [
    { id: 'tone_too_formal', label: 'Too formal' },
    { id: 'tone_too_casual', label: 'Too casual' },
    { id: 'tone_too_aggressive', label: 'Too aggressive' },
    { id: 'tone_too_weak', label: 'Not assertive enough' }
  ],
  structure: [
    { id: 'wrong_format', label: 'Wrong format' },
    { id: 'too_long', label: 'Too verbose' },
    { id: 'too_short', label: 'Too brief' },
    { id: 'missing_sections', label: 'Missing sections' }
  ],
  accuracy: [
    { id: 'wrong_facts', label: 'Wrong facts' },
    { id: 'wrong_dates', label: 'Wrong dates' },
    { id: 'wrong_references', label: 'Wrong legal references' }
  ]
};

const SEVERITY_LEVELS = [
  { id: 'minor', label: 'Minor - small tweaks, would have been OK' },
  { id: 'moderate', label: 'Moderate - needed improvement but approach was right' },
  { id: 'major', label: 'Major - significant rewrite needed' },
  { id: 'rewrite', label: 'Complete rewrite - had to start over' }
];

/**
 * Draft Review Modal Component
 * Modal for reviewing, editing, approving or rejecting AI-generated drafts
 */
function DraftReviewModal({
  task,
  onClose,
  onApprove,
  onReject,
  isApproving,
  isRejecting
}) {
  const { t } = useTranslation('attorney');
  const queryClient = useQueryClient();
  const { canMessageClients } = useAttorney();
  const [mode, setMode] = useState('view'); // view, edit, reject
  const [deadlineCreated, setDeadlineCreated] = useState(false);
  const [showRequestInfoModal, setShowRequestInfoModal] = useState(false);

  // Create deadline mutation
  const createDeadlineMutation = useMutation({
    mutationFn: (deadlineData) => deadlineService.createDeadline(deadlineData),
    onSuccess: () => {
      setDeadlineCreated(true);
      queryClient.invalidateQueries({ queryKey: ['deadlines'] });
    }
  });

  const handleCreateDeadline = () => {
    // Calculate default deadline: 60 days from now
    const defaultDeadline = new Date();
    defaultDeadline.setDate(defaultDeadline.getDate() + 60);

    const deadlineData = {
      action: 'create',
      userId: task.userId,
      applicationId: task.applicationId,
      taskId: task.id,
      correspondenceId: task.correspondenceId,
      type: 'response_deadline',
      title: `Response Deadline - ${getTaskTitle()}`,
      description: taskSummary || 'Office action response deadline',
      dueDate: defaultDeadline.toISOString(),
      jurisdiction: task.correspondence?.jurisdiction || task.input?.correspondence?.jurisdiction || null
    };

    createDeadlineMutation.mutate(deadlineData);
  };

  // Support both task.result?.X and task.X field locations
  const taskDraft = task.draft || task.result?.draft || '';
  const taskSummary = task.summary || task.result?.summary || '';
  const rawSuccessLikelihood = task.successLikelihood ?? task.result?.successLikelihood;
  // Normalize to 0-100 scale (handle both 0-1 and 0-100 inputs)
  const taskSuccessLikelihood = rawSuccessLikelihood !== undefined
    ? (rawSuccessLikelihood > 1 ? rawSuccessLikelihood : rawSuccessLikelihood * 100)
    : undefined;
  const taskKeyArguments = task.keyArguments || task.result?.keyArguments || [];
  const taskSimilarCases = task.similarCasesUsed || task.result?.similarCasesUsed || [];

  // Build proper title from task data
  const getTaskTitle = () => {
    const classification = task.input?.classification?.subType || task.input?.classification?.classification;
    const ipRef = task.correspondence?.ipOfficeRef || task.input?.correspondence?.ipOfficeRef;
    const trademarkName = task.trademarkName || task.input?.correspondence?.trademarkName;

    let title = '';
    if (classification === 'adverse_examination') {
      title = 'Adverse Report';
    } else if (classification === 'office_action') {
      title = 'Office Action';
    } else if (classification === 'opposition') {
      title = 'Opposition';
    } else {
      title = classification || 'Response';
    }

    if (ipRef) title += ` - ${ipRef}`;
    if (trademarkName) title += ` - ${trademarkName}`;

    return title;
  };

  const [editedDraft, setEditedDraft] = useState(taskDraft);
  const [rejectReason, setRejectReason] = useState('');

  // AI Learning feedback state
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [severity, setSeverity] = useState('');
  const [learningNotes, setLearningNotes] = useState('');

  const hasChanges = editedDraft !== taskDraft;

  // Toggle a feedback category
  const toggleCategory = (categoryId) => {
    setSelectedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(c => c !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleApprove = () => {
    // Build modifications object with feedback for AI learning
    let modifications = null;

    if (hasChanges) {
      modifications = {
        modifiedDraft: editedDraft,
        originalDraft: taskDraft,
        // AI Learning feedback
        feedback: {
          categories: selectedCategories,
          severity: severity || 'minor',
          notes: learningNotes.trim() || null
        }
      };
    }

    onApprove(modifications);
  };

  // Check if feedback is required (when there are changes)
  const canApproveWithChanges = !hasChanges || (severity && selectedCategories.length > 0);

  const handleReject = () => {
    if (rejectReason.trim()) {
      onReject(rejectReason.trim());
    }
  };

  const similarCases = taskSimilarCases;
  const keyArguments = taskKeyArguments;

  return (
    <div className="draft-review-modal-overlay" onClick={onClose}>
      <div className="draft-review-modal" onClick={e => e.stopPropagation()}>
        <div className="draft-review-modal-header">
          <button className="draft-review-modal-close" onClick={onClose}>✕</button>
          <h2>{getTaskTitle() || t('attorney.draftReview.title', 'Review AI Draft')}</h2>
          <div className="draft-review-meta">
            <span className="draft-review-app-id">{task.applicationId}</span>
            {task.clientName && <span className="draft-review-client">{task.clientName}</span>}
          </div>
        </div>

        <div className="draft-review-modal-content">
          {/* Summary Section */}
          <section className="draft-review-section">
            <h3>{t('attorney.draftReview.summary', 'Summary')}</h3>
            <p className="draft-summary">{taskSummary || 'No summary available'}</p>

            {taskSuccessLikelihood !== undefined && (
              <div className="success-likelihood">
                <span className="likelihood-label">
                  {t('attorney.draftReview.estimatedSuccess', 'Estimated Success')}:
                </span>
                <div className="likelihood-bar">
                  <div
                    className="likelihood-fill"
                    style={{ width: `${taskSuccessLikelihood}%` }}
                  />
                </div>
                <span className="likelihood-value">
                  {Math.round(taskSuccessLikelihood)}%
                </span>
              </div>
            )}

            {/* Create Deadline Button - only show if no deadline exists */}
            {!task.deadlineId && (
              <div className="create-deadline-section">
                {deadlineCreated ? (
                  <span className="deadline-created-badge">
                    {t('attorney.draftReview.deadlineCreated', 'Deadline Created')}
                  </span>
                ) : (
                  <button
                    className="btn-create-deadline"
                    onClick={handleCreateDeadline}
                    disabled={createDeadlineMutation.isPending}
                  >
                    {createDeadlineMutation.isPending
                      ? t('attorney.draftReview.creatingDeadline', 'Creating...')
                      : t('attorney.draftReview.createDeadline', 'Create Deadline (60 days)')}
                  </button>
                )}
                {createDeadlineMutation.isError && (
                  <span className="deadline-error">
                    {t('attorney.draftReview.deadlineError', 'Failed to create deadline')}
                  </span>
                )}
              </div>
            )}
          </section>

          {/* Key Arguments */}
          {keyArguments.length > 0 && (
            <section className="draft-review-section">
              <h3>{t('attorney.draftReview.keyArguments', 'Key Arguments')}</h3>
              <ul className="key-arguments-list">
                {keyArguments.map((arg, index) => (
                  <li key={index}>{arg}</li>
                ))}
              </ul>
            </section>
          )}

          {/* Similar Cases Used */}
          {similarCases.length > 0 && (
            <section className="draft-review-section">
              <h3>{t('attorney.draftReview.similarCases', 'Similar Cases Referenced')}</h3>
              <div className="similar-cases">
                {similarCases.map((caseId, index) => (
                  <span key={index} className="similar-case-tag">{caseId}</span>
                ))}
              </div>
            </section>
          )}

          {/* Draft Content */}
          <section className="draft-review-section draft-content-section">
            <div className="draft-content-header">
              <h3>{t('attorney.draftReview.draftContent', 'Draft Content')}</h3>
              <div className="draft-mode-tabs">
                <button
                  className={`mode-tab ${mode === 'view' ? 'active' : ''}`}
                  onClick={() => setMode('view')}
                >
                  {t('attorney.draftReview.view', 'View')}
                </button>
                <button
                  className={`mode-tab ${mode === 'edit' ? 'active' : ''}`}
                  onClick={() => setMode('edit')}
                >
                  {t('attorney.draftReview.edit', 'Edit')}
                </button>
              </div>
            </div>

            {mode === 'view' ? (
              <div className="draft-view">
                <pre>{taskDraft || 'No draft content available'}</pre>
              </div>
            ) : (
              <textarea
                className="draft-edit"
                value={editedDraft}
                onChange={(e) => setEditedDraft(e.target.value)}
                placeholder={t('attorney.draftReview.editPlaceholder', 'Edit the draft...')}
              />
            )}
          </section>

          {/* AI Learning Feedback Section (shown when editing and there are changes) */}
          {mode === 'edit' && hasChanges && (
            <section className="draft-review-section feedback-section">
              <h3>
                {t('attorney.draftReview.feedbackTitle', 'AI Improvement Feedback')}
                <span className="required-badge">{t('attorney.draftReview.required', 'Required')}</span>
              </h3>
              <p className="feedback-description">
                {t('attorney.draftReview.feedbackDescription', 'Help the AI learn by explaining what needed improvement.')}
              </p>

              {/* Feedback Categories */}
              <div className="feedback-categories">
                <div className="feedback-category-group">
                  <h4>{t('attorney.draftReview.content', 'Content')}</h4>
                  <div className="category-checkboxes">
                    {FEEDBACK_CATEGORIES.content.map(cat => (
                      <label key={cat.id} className="category-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedCategories.includes(cat.id)}
                          onChange={() => toggleCategory(cat.id)}
                        />
                        <span>{t(`attorney.draftReview.feedback.${cat.id}`, cat.label)}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="feedback-category-group">
                  <h4>{t('attorney.draftReview.toneStyle', 'Tone & Style')}</h4>
                  <div className="category-checkboxes">
                    {FEEDBACK_CATEGORIES.tone.map(cat => (
                      <label key={cat.id} className="category-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedCategories.includes(cat.id)}
                          onChange={() => toggleCategory(cat.id)}
                        />
                        <span>{t(`attorney.draftReview.feedback.${cat.id}`, cat.label)}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="feedback-category-group">
                  <h4>{t('attorney.draftReview.structure', 'Structure')}</h4>
                  <div className="category-checkboxes">
                    {FEEDBACK_CATEGORIES.structure.map(cat => (
                      <label key={cat.id} className="category-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedCategories.includes(cat.id)}
                          onChange={() => toggleCategory(cat.id)}
                        />
                        <span>{t(`attorney.draftReview.feedback.${cat.id}`, cat.label)}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="feedback-category-group">
                  <h4>{t('attorney.draftReview.accuracy', 'Accuracy')}</h4>
                  <div className="category-checkboxes">
                    {FEEDBACK_CATEGORIES.accuracy.map(cat => (
                      <label key={cat.id} className="category-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedCategories.includes(cat.id)}
                          onChange={() => toggleCategory(cat.id)}
                        />
                        <span>{t(`attorney.draftReview.feedback.${cat.id}`, cat.label)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Severity Level */}
              <div className="feedback-severity">
                <h4>{t('attorney.draftReview.severityQuestion', 'How significant were your changes?')}</h4>
                <div className="severity-options">
                  {SEVERITY_LEVELS.map(level => (
                    <label key={level.id} className="severity-option">
                      <input
                        type="radio"
                        name="severity"
                        value={level.id}
                        checked={severity === level.id}
                        onChange={() => setSeverity(level.id)}
                      />
                      <span>{t(`attorney.draftReview.severity.${level.id}`, level.label)}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Learning Notes */}
              <div className="feedback-notes">
                <h4>{t('attorney.draftReview.learningNotesTitle', 'What should the AI learn from this?')}</h4>
                <textarea
                  className="learning-notes-input"
                  value={learningNotes}
                  onChange={(e) => setLearningNotes(e.target.value)}
                  placeholder={t('attorney.draftReview.learningNotesPlaceholder', 'Optional: Explain what the AI should do differently next time...')}
                />
              </div>
            </section>
          )}

          {/* Reject Reason (shown when rejecting) */}
          {mode === 'reject' && (
            <section className="draft-review-section reject-section">
              <h3>{t('attorney.draftReview.rejectReason', 'Reason for Rejection')}</h3>
              <textarea
                className="reject-reason-input"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder={t('attorney.draftReview.rejectPlaceholder', 'Explain why this draft is being rejected...')}
              />
            </section>
          )}
        </div>

        <div className="draft-review-modal-footer">
          {mode === 'reject' ? (
            <>
              <button className="btn-back" onClick={() => setMode('view')}>
                {t('attorney.draftReview.back', 'Back')}
              </button>
              <button
                className="btn-confirm-reject"
                onClick={handleReject}
                disabled={!rejectReason.trim() || isRejecting}
              >
                {isRejecting
                  ? t('attorney.draftReview.rejecting', 'Rejecting...')
                  : t('attorney.draftReview.confirmReject', 'Confirm Rejection')}
              </button>
            </>
          ) : (
            <>
              <button
                className="btn-request-info"
                onClick={() => setShowRequestInfoModal(true)}
                disabled={isApproving || isRejecting}
              >
                {canMessageClients
                  ? t('attorney.draftReview.requestClientInfo', 'Request Client Info')
                  : t('attorney.draftReview.requestViaMarkoTM', 'Request via MarkoTM')}
              </button>
              <button
                className="btn-reject"
                onClick={() => setMode('reject')}
                disabled={isApproving || isRejecting}
              >
                {t('attorney.draftReview.reject', 'Reject')}
              </button>
              <button
                className="btn-approve"
                onClick={handleApprove}
                disabled={isApproving || isRejecting || (hasChanges && !canApproveWithChanges)}
                title={hasChanges && !canApproveWithChanges
                  ? t('attorney.draftReview.feedbackRequired', 'Please provide feedback on what needed improvement')
                  : ''}
              >
                {isApproving
                  ? t('attorney.draftReview.approving', 'Approving...')
                  : hasChanges
                    ? t('attorney.draftReview.approveWithChanges', 'Approve with Changes')
                    : t('attorney.draftReview.approve', 'Approve')}
              </button>
            </>
          )}
        </div>
      </div>

        {/* Request Info Modal - shows appropriate modal based on attorney type */}
        {showRequestInfoModal && (
          canMessageClients ? (
            <MessageComposeModal
              isOpen={showRequestInfoModal}
              onClose={() => setShowRequestInfoModal(false)}
              applicationId={task.applicationId}
              clientName={task.clientName}
            />
          ) : (
            <RequestViaMarkoTMModal
              isOpen={showRequestInfoModal}
              onClose={() => setShowRequestInfoModal(false)}
              applicationId={task.applicationId}
              clientName={task.clientName}
            />
          )
        )}
    </div>
  );
}

export default DraftReviewModal;
